"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import Container from "@/components/Container";
import Logo from "@/components/Logo";
import HeaderBanner from "./HeaderBanner";
import HeaderNavigationMobile, { MOBILE_NAVIGATION_ID } from "./HeaderNavigationMobile";
import HeaderNavigationDesktop from "./HeaderNavigationDesktop";
import SearchButton from "@/components/SearchButton";
import CartButton from "@/components/Cart/CartButton";
import useElementHeight from "@/tools/hooks/useElementHeight";
import useSessionUser from "@/hooks/useSessionUser";
import useHeaderState from "./hooks/useHeaderState";
import classNames from "@/helpers/classNames";
import type { IHeaderDocument } from "@/tools/sanity/schema/documents/headerDocument";
import type { IHeaderBannerDocument } from "@/tools/sanity/schema/documents/headerBannerDocument";
import styles from "./styles.module.scss";
import useMediaQuery from "@/hooks/useMediaQuery";

interface HeaderProps extends IHeaderDocument, IHeaderBannerDocument {
  className?: string;
  featuredSearches?: any;
}

const Header = (props: HeaderProps) => {
  const { className, header, headerBanner, featuredSearches } = props;
  const pathname = usePathname();
  const { showSearch, setShowSearch, mobileNavOpen, setMobileNavOpen, hidden, atTop } =
    useHeaderState();
  // Reports false without a request while `NEXT_PUBLIC_DISABLE_PORTAL` is set,
  // so the nav below falls back to the CMS "Login" button on its own.
  const { isVerified } = useSessionUser();
  const [theme, setTheme] = useState<"yellow" | "light" | "dark" | "white">("yellow");
  const classes = classNames(
    styles.header,
    { [styles.hidden]: hidden },
    { [styles.atTop]: atTop },
    className,
  );

  const headerElRef = useRef<HTMLElement>(null);
  const isSmallDesktopDown = useMediaQuery({ down: "smallDesktop" });
  const isStudio = pathname?.startsWith("/studio");
  const _isHome = pathname === "/";
  const isSearchPage = pathname?.startsWith("/search");

  // Calculate header height and set as a css variable on the root element named --header-height
  // Update this value on resize
  const [headerRef, headerHeight] = useElementHeight({});

  useEffect(() => {
    setTheme(mobileNavOpen ? "white" : "yellow");
  }, [mobileNavOpen]);

  useEffect(() => {
    const closeMobileNav = mobileNavOpen && isSmallDesktopDown;
    setMobileNavOpen(closeMobileNav);
  }, [setMobileNavOpen, mobileNavOpen, isSmallDesktopDown]);

  useEffect(() => {
    document.documentElement.style.setProperty("--header-height", `${headerHeight}px`);
  }, [headerHeight]);

  useEffect(() => {
    const updateHeaderTop = () => {
      if (headerElRef.current) {
        const { bottom } = headerElRef.current.getBoundingClientRect();
        document.documentElement.style.setProperty("--header-top", `${Math.max(0, bottom)}px`);
      }
    };

    window.addEventListener("scroll", updateHeaderTop);
    updateHeaderTop();

    const observer = new ResizeObserver(updateHeaderTop);
    if (headerRef?.current) {
      observer.observe(headerRef.current);
    }

    return () => {
      window.removeEventListener("scroll", updateHeaderTop);
      observer.disconnect();
    };
  }, [headerRef]);

  useEffect(() => {
    if (hidden) {
      document.documentElement.dataset.headerHidden = "true";
    } else {
      delete document.documentElement.dataset.headerHidden;
    }
  }, [hidden]);

  if (isStudio) {
    return null;
  }

  return (
    <div ref={headerRef}>
      <HeaderBanner headerBanner={headerBanner} />
      <header ref={headerElRef} className={classes} data-theme={theme}>
        <div className={styles.wrapper}>
          <Container className={styles.container}>
            <div className={styles.logoContainer}>
              <Logo className={styles.logo} variant="wordmark" />
            </div>
            <HeaderNavigationDesktop
              header={header}
              className={styles.headerNavDesktop}
              setTheme={setTheme}
              featuredSearches={featuredSearches}
              isSearchPage={isSearchPage}
              isSearchActive={showSearch}
              setIsSearchActive={setShowSearch}
              hasPortalAccess={isVerified}
            />
            <HeaderNavigationMobile
              header={header}
              className={styles.headerNavMobile}
              open={mobileNavOpen}
              hasPortalAccess={isVerified}
            />

            <div className={styles.mobileActions}>
              {process.env.NEXT_PUBLIC_ENABLE_CART === "true" && <CartButton />}
              <div className={styles.mobileSearchButton}>
                <SearchButton isActive={showSearch} onClick={() => setShowSearch(!showSearch)} />
              </div>
              <button
                type="button"
                aria-label={mobileNavOpen ? "Close mobile navigation" : "Open mobile navigation"}
                aria-expanded={mobileNavOpen}
                aria-controls={MOBILE_NAVIGATION_ID}
                onClick={() => setMobileNavOpen(!mobileNavOpen)}
                className={classNames(styles.mobileNavToggle, {
                  [styles.active]: mobileNavOpen,
                })}
              >
                <span />
                <span />
                <span />
              </button>
            </div>
          </Container>
        </div>
      </header>
      <a id="content" tabIndex={-1} />
    </div>
  );
};

export default Header;
